import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faMoneyBillWave,
  faSearch,
  faSpinner,
  faCheck,
  faTimes,
  faExclamationTriangle,
  faTicketAlt,
  faIdBadge,
  faCalendar,
  faChartLine,
  faChartPie,
  faUsers,
  faFileAlt,
  faFilter,
  faChevronLeft,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { useTranslation } from 'react-i18next';
import config from '../config/config';
import i18n, { intlLocale } from '../i18n/i18n';
import { translateServerMessage } from '../i18n/serverMessages';
import './RefundManagementPage.css';

// For the data loading: the hook's `t` changes with the language, and the loaders run from the
// mount effect, so they read the text through i18n directly.
const tr = (key, options) => i18n.t(key, { ns: 'refunds', ...options });

// The browser's own errors (fetch failing while offline, a reply that isn't JSON) are in English
// whatever language the page is in. English shows them as they are; Spanish shows its own text.
// Messages we threw ourselves, already translated, pass through.
const errorDetail = (err) => {
  const message = (err && err.message) || '';
  if (err instanceof TypeError && /fetch|network|load failed/i.test(message)) {
    return tr('errors.connection', { message });
  }
  if (err instanceof SyntaxError) {
    return tr('errors.unexpectedResponse', { message });
  }
  return message;
};

// An enum value from the API (ACTIVE, CASH, FULL…) in the admin's language; one the page doesn't
// know is shown as it is. The raw value is still what the filters and the API use.
const enumLabel = (group, value) => (value ? tr(`${group}.${value}`, { defaultValue: value }) : value);

// "noviembre de 2026" → "Noviembre de 2026"; English month names are capitalized already.
const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1);

const RefundManagementPage = () => {
  const { t } = useTranslation('refunds');
  const navigate = useNavigate();
  const { getAccessToken } = useAuth();

  // Statistics state
  const [statistics, setStatistics] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  // Recent refundable tickets state
  const [recentTickets, setRecentTickets] = useState([]);
  const [recentTicketsPage, setRecentTicketsPage] = useState(0);
  const [recentTicketsTotalPages, setRecentTicketsTotalPages] = useState(0);
  const [recentTicketsTotalElements, setRecentTicketsTotalElements] = useState(0);
  const [loadingRecentTickets, setLoadingRecentTickets] = useState(false);

  // Search state
  const [searchTab, setSearchTab] = useState('event'); // 'event' or 'user'
  
  // Event search state
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [selectedEventForSearch, setSelectedEventForSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [eventTickets, setEventTickets] = useState([]);
  const [eventTicketsPage, setEventTicketsPage] = useState(0);
  const [eventTicketsTotalPages, setEventTicketsTotalPages] = useState(0);
  const [eventTicketsTotalElements, setEventTicketsTotalElements] = useState(0);
  const [loadingEventTickets, setLoadingEventTickets] = useState(false);

  // User search state
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [selectedTeam, setSelectedTeam] = useState('');
  const [teams, setTeams] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedEventForUser, setSelectedEventForUser] = useState(null);
  const [userTickets, setUserTickets] = useState([]);
  const [userTicketsPage, setUserTicketsPage] = useState(0);
  const [userTicketsTotalPages, setUserTicketsTotalPages] = useState(0);
  const [userTicketsTotalElements, setUserTicketsTotalElements] = useState(0);
  const [loadingUserSearch, setLoadingUserSearch] = useState(false);
  const [loadingUserTickets, setLoadingUserTickets] = useState(false);

  // Refund modal state (keep existing modal)
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [selectedTicketForRefund, setSelectedTicketForRefund] = useState(null);
  const [numberOfTickets, setNumberOfTickets] = useState(1);
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Refunds list state
  const [refunds, setRefunds] = useState([]);
  const [refundsPage, setRefundsPage] = useState(0);
  const [refundsTotalPages, setRefundsTotalPages] = useState(0);
  const [refundsTotalElements, setRefundsTotalElements] = useState(0);
  const [loadingRefunds, setLoadingRefunds] = useState(false);
  const [refundsStatusFilter, setRefundsStatusFilter] = useState('');
  
  // Ticket details modal state
  const [showTicketDetailModal, setShowTicketDetailModal] = useState(false);
  const [selectedTicketDetails, setSelectedTicketDetails] = useState(null);
  
  // Common state
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [refundResult, setRefundResult] = useState(null);

  // Fetch statistics on mount
  useEffect(() => {
    fetchStatistics();
    fetchRecentTickets(0);
    fetchRefunds(0, '');
    fetchEvents();
    fetchTeams();
  }, []);

  const fetchStatistics = async () => {
    try {
      setLoadingStats(true);
      const token = await getAccessToken();
      if (!token) throw new Error(tr('errors.noToken'));
      
      const response = await fetch(`${config.API_BASE_URL}/em/v1/admin/tickets/refund/statistics`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(translateServerMessage(errorData.message, tr('errors.fetchStatistics')));
      }
      
      const data = await response.json();
      setStatistics(data.data);
    } catch (err) {
      console.error('Error fetching statistics:', err);
      // Don't show error for stats, just log it
    } finally {
      setLoadingStats(false);
    }
  };

  const fetchRecentTickets = async (page) => {
    try {
      setLoadingRecentTickets(true);
      setError(null);
      const token = await getAccessToken();
      if (!token) throw new Error(tr('errors.noToken'));

      const response = await fetch(`${config.API_BASE_URL}/em/v1/admin/tickets/refundable?page=${page}&size=10`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(translateServerMessage(errorData.message, tr('errors.fetchRefundable')));
      }

      const data = await response.json();
      setRecentTickets(data.data.content || []);
      setRecentTicketsTotalPages(data.data.totalPages || 0);
      setRecentTicketsTotalElements(data.data.totalElements || 0);
      setRecentTicketsPage(page);
    } catch (err) {
      console.error('Error fetching recent tickets:', err);
      setError(errorDetail(err));
    } finally {
      setLoadingRecentTickets(false);
    }
  };

  const fetchRefunds = async (page, status) => {
    try {
      setLoadingRefunds(true);
      setError(null);
      const token = await getAccessToken();
      if (!token) throw new Error(tr('errors.noToken'));

      const params = new URLSearchParams({ page, size: 10 });
      if (status && status !== '') params.append('status', status);

      const response = await fetch(`${config.API_BASE_URL}/em/v1/admin/tickets/refunds?${params}`, {
          method: 'GET',
          headers: {
          'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(translateServerMessage(errorData.message, tr('errors.fetchRefunds')));
      }

      const data = await response.json();
      setRefunds(data.data.content || []);
      setRefundsTotalPages(data.data.totalPages || 0);
      setRefundsTotalElements(data.data.totalElements || 0);
      setRefundsPage(page);
    } catch (err) {
      console.error('Error fetching refunds:', err);
      setError(errorDetail(err));
    } finally {
      setLoadingRefunds(false);
    }
  };

  const fetchEvents = async () => {
    try {
      setLoadingEvents(true);
      const token = await getAccessToken();
      if (!token) throw new Error(tr('errors.noToken'));

      const response = await fetch(`${config.API_BASE_URL}/em/v1/admin/events/all`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(translateServerMessage(errorData.message, tr('errors.fetchEvents')));
      }

      const data = await response.json();
      const eventsList = Array.isArray(data.data) ? data.data : (data.data ? [data.data] : []);
      setEvents(eventsList);
    } catch (err) {
      console.error('Error fetching events:', err);
    } finally {
      setLoadingEvents(false);
    }
  };

  const fetchTeams = async () => {
    try {
      setLoadingTeams(true);
      const token = await getAccessToken();
      if (!token) throw new Error(tr('errors.noToken'));

      const response = await fetch(`${config.API_BASE_URL}/em/v1/admin/teams`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(translateServerMessage(errorData.message, tr('errors.fetchTeams')));
      }

      const data = await response.json();
      setTeams(data.data || []);
    } catch (err) {
      console.error('Error fetching teams:', err);
    } finally {
      setLoadingTeams(false);
    }
  };

  const fetchEventTickets = async (eventId, status, page) => {
    try {
      setLoadingEventTickets(true);
      setError(null);
      const token = await getAccessToken();
      if (!token) throw new Error(tr('errors.noToken'));

      const params = new URLSearchParams({ page, size: 10 });
      if (status) params.append('status', status);

      const response = await fetch(`${config.API_BASE_URL}/em/v1/admin/tickets/by-event/${eventId}?${params}`, {
          method: 'GET',
          headers: {
          'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(translateServerMessage(errorData.message, tr('errors.fetchTickets')));
      }

      const data = await response.json();
      setEventTickets(data.data.content || []);
      setEventTicketsTotalPages(data.data.totalPages || 0);
      setEventTicketsTotalElements(data.data.totalElements || 0);
      setEventTicketsPage(page);
    } catch (err) {
      console.error('Error fetching event tickets:', err);
      setError(errorDetail(err));
    } finally {
      setLoadingEventTickets(false);
    }
  };

  const searchUsers = async () => {
    if (!userSearchQuery.trim()) {
      setError(t('errors.enterSearchTerm'));
          return;
        }

    try {
      setLoadingUserSearch(true);
      setError(null);
      setUserSearchResults([]);
      setSelectedUser(null);
      setUserTickets([]);

      const token = await getAccessToken();
      if (!token) throw new Error(tr('errors.noToken'));

      const params = new URLSearchParams({ search: userSearchQuery.trim() });
      if (selectedTeam) params.append('teamName', selectedTeam);

      const response = await fetch(`${config.API_BASE_URL}/em/v1/admin/tickets/users/search?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(translateServerMessage(errorData.message, tr('errors.searchUsers')));
      }

      const data = await response.json();
      setUserSearchResults(data.data || []);
      if (data.data.length === 0) {
        setError(tr('errors.noUsersFound'));
      }
    } catch (err) {
      console.error('Error searching users:', err);
      setError(errorDetail(err));
    } finally {
      setLoadingUserSearch(false);
    }
  };

  const fetchUserTickets = async (iboNumber, eventId, page) => {
    try {
      setLoadingUserTickets(true);
      setError(null);
      const token = await getAccessToken();
      if (!token) throw new Error(tr('errors.noToken'));

      const params = new URLSearchParams({ page, size: 10 });
      if (eventId) params.append('eventId', eventId);

      const response = await fetch(`${config.API_BASE_URL}/em/v1/admin/tickets/by-user/${iboNumber}?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(translateServerMessage(errorData.message, tr('errors.fetchUserTickets')));
      }

      const data = await response.json();
      setUserTickets(data.data.content || []);
      setUserTicketsTotalPages(data.data.totalPages || 0);
      setUserTicketsTotalElements(data.data.totalElements || 0);
      setUserTicketsPage(page);
    } catch (err) {
      console.error('Error fetching user tickets:', err);
      setError(errorDetail(err));
    } finally {
      setLoadingUserTickets(false);
    }
  };

  const handleOpenRefundModal = (ticket) => {
    setSelectedTicketForRefund(ticket);
    setShowRefundModal(true);
    setNumberOfTickets(1);
    setReason('');
    setNotes('');
    setConfirmChecked(false);
    setError(null);
  };

  const handleCloseRefundModal = () => {
    setShowRefundModal(false);
    setSelectedTicketForRefund(null);
    setNumberOfTickets(1);
    setReason('');
    setNotes('');
    setConfirmChecked(false);
    setError(null);
  };

  const handleOpenTicketDetailModal = (ticket) => {
    setSelectedTicketDetails(ticket);
    setShowTicketDetailModal(true);
    setError(null);
  };

  const handleCloseTicketDetailModal = () => {
    setShowTicketDetailModal(false);
    setSelectedTicketDetails(null);
  };

  const handleProcessRefund = async () => {
    if (!confirmChecked) {
      setError(t('errors.confirmRequired'));
      return;
    }

    if (numberOfTickets < 1 || numberOfTickets > selectedTicketForRefund.noOfTickets) {
      setError(t('errors.invalidQuantity', { max: selectedTicketForRefund.noOfTickets }));
      return;
    }

    try {
      setProcessing(true);
      setError(null);

      const token = await getAccessToken();
      if (!token) throw new Error(tr('errors.noToken'));

      const requestBody = {
        ticketId: parseInt(selectedTicketForRefund.ticketId),
        numberOfTickets: parseInt(numberOfTickets),
        reason: reason.trim() || undefined,
        notes: notes.trim() || undefined
      };

      const response = await fetch(`${config.API_BASE_URL}/em/v1/admin/tickets/refund`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(translateServerMessage(data.message, tr('errors.processRefund')));
      }

      if (data.status === 'success') {
        setRefundResult(data.data);
        setSuccessMessage(t('success.refundProcessed'));
        setShowRefundModal(false);
        
        // Refresh data
        fetchStatistics();
        fetchRecentTickets(recentTicketsPage);
        fetchRefunds(refundsPage, refundsStatusFilter);

        // Clear success message after 10 seconds
        setTimeout(() => setSuccessMessage(''), 10000);
      } else {
        throw new Error(translateServerMessage(data.message, tr('errors.processRefund')));
      }

    } catch (err) {
      console.error('Error processing refund:', err);
      setError(errorDetail(err));
    } finally {
      setProcessing(false);
    }
  };

  const handleEventSearchSubmit = () => {
    if (!selectedEventForSearch) {
      setError(t('errors.selectEvent'));
      return;
    }
    fetchEventTickets(selectedEventForSearch, statusFilter, 0);
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setSelectedEventForUser(null);
    fetchUserTickets(user.iboNumber, null, 0);
  };

  const handleEventFilterForUser = (eventId) => {
    setSelectedEventForUser(eventId);
    if (selectedUser) {
      fetchUserTickets(selectedUser.iboNumber, eventId, 0);
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return t('notAvailable');
    let date;
    if (typeof timestamp === 'number') {
      date = new Date(timestamp * 1000);
    } else {
      date = new Date(timestamp);
    }
    
    if (isNaN(date.getTime())) return t('notAvailable');
    
    return date.toLocaleDateString(intlLocale(), {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    // No thousands separator, as the page always showed it ("$1234.50").
    const format = new Intl.NumberFormat(intlLocale(), {
      style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: false
    });
    return format.format(amount != null ? Number(amount) : 0);
  };

  const getPaymentMethodBadge = (method) => {
    const colors = {
      CASH: '#4caf50',
      SQUARE: '#5469d4',
      CARD: '#5469d4'
    };
    return (
      <span className="payment-badge" style={{ backgroundColor: colors[method] || '#666' }}>
        {enumLabel('paymentMethods', method)}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const config = {
      ACTIVE: { color: '#4caf50', text: t('ticketStatus.ACTIVE') },
      USED: { color: '#2196f3', text: t('ticketStatus.USED') },
      CANCELLED: { color: '#f44336', text: t('ticketStatus.CANCELLED') },
      EXPIRED: { color: '#9e9e9e', text: t('ticketStatus.EXPIRED') },
      TRANSFERRED: { color: '#ff9800', text: t('ticketStatus.TRANSFERRED') }
    };

    const statusConfig = config[status] || { color: '#666', text: status };

    return (
      <span className="status-badge" style={{ backgroundColor: statusConfig.color }}>
        {statusConfig.text}
      </span>
    );
  };

  const getRefundStatusBadge = (status) => {
    const config = {
      COMPLETED: { color: '#4caf50', text: t('refundStatus.COMPLETED'), icon: faCheck },
      PENDING: { color: '#ff9800', text: t('refundStatus.PENDING'), icon: faSpinner },
      FAILED: { color: '#f44336', text: t('refundStatus.FAILED'), icon: faTimes },
      CANCELLED: { color: '#9e9e9e', text: t('refundStatus.CANCELLED'), icon: faTimes }
    };

    const statusConfig = config[status] || { color: '#666', text: status, icon: faFileAlt };

    return (
      <span className="refund-status-badge" style={{ backgroundColor: statusConfig.color }}>
        <FontAwesomeIcon icon={statusConfig.icon} />
        {statusConfig.text}
      </span>
    );
  };

  const getEstimatedRefundTotal = () => {
    if (!selectedTicketForRefund || !numberOfTickets) return 0;
    const perTicketTotal = selectedTicketForRefund.totalCost / selectedTicketForRefund.noOfTickets;
    return (perTicketTotal * numberOfTickets).toFixed(2);
  };

  // Chart colors
  const COLORS = ['#4caf50', '#ff9800', '#f44336', '#2196f3'];

  return (
    <div className="refund-management-container">
      {/* Header */}
      <header className="refund-header">
        <button onClick={handleBack} className="back-button">
          <FontAwesomeIcon icon={faArrowLeft} /> {t('backToDashboard')}
        </button>
        <h2><FontAwesomeIcon icon={faMoneyBillWave} /> {t('title')}</h2>
      </header>

      {/* Success Message */}
      {successMessage && (
        <div className="success-message">
          <FontAwesomeIcon icon={faCheck} />
          {successMessage}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="error-message">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          {error}
        </div>
      )}

      {/* Statistics Dashboard */}
      <section className="statistics-section">
        <h3><FontAwesomeIcon icon={faChartLine} /> {t('stats.title')}</h3>
        
        {loadingStats ? (
          <div className="loading-container">
            <FontAwesomeIcon icon={faSpinner} spin size="2x" />
          </div>
        ) : statistics ? (
          <>
            <div className="stats-cards">
              <div className="stat-card total">
                <div className="stat-icon">
                  <FontAwesomeIcon icon={faMoneyBillWave} />
                </div>
                <div className="stat-content">
                  <div className="stat-value">{statistics.totalRefunds}</div>
                  <div className="stat-label">{t('stats.total')}</div>
                  <div className="stat-amount">{formatCurrency(statistics.totalAmountRefunded)}</div>
                </div>
              </div>
              
              <div className="stat-card completed">
                <div className="stat-icon">
                  <FontAwesomeIcon icon={faCheck} />
                </div>
                <div className="stat-content">
                  <div className="stat-value">{statistics.completedRefunds}</div>
                  <div className="stat-label">{t('stats.completed')}</div>
                  <div className="stat-amount">{formatCurrency(statistics.completedAmount)}</div>
                </div>
              </div>
              
              <div className="stat-card pending">
                <div className="stat-icon">
                  <FontAwesomeIcon icon={faSpinner} />
                </div>
                <div className="stat-content">
                  <div className="stat-value">{statistics.pendingRefunds}</div>
                  <div className="stat-label">{t('stats.pending')}</div>
                  <div className="stat-amount">{formatCurrency(statistics.pendingAmount)}</div>
                </div>
              </div>
              
              <div className="stat-card failed">
                <div className="stat-icon">
                  <FontAwesomeIcon icon={faTimes} />
                </div>
                <div className="stat-content">
                  <div className="stat-value">{statistics.failedRefunds}</div>
                  <div className="stat-label">{t('stats.failed')}</div>
                  <div className="stat-amount">{formatCurrency(statistics.failedAmount)}</div>
                </div>
              </div>
            </div>

            <div className="stats-charts">
              <div className="chart-container">
                <h4>{t('stats.byStatus')}</h4>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: t('stats.completed'), value: statistics.completedRefunds },
                        { name: t('stats.pending'), value: statistics.pendingRefunds },
                        { name: t('stats.failed'), value: statistics.failedRefunds }
                      ]}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => t('stats.pieLabel', { name, percent: (percent * 100).toFixed(0) })}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {[0, 1, 2].map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="chart-container">
                <h4>{t('stats.trends')}</h4>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart
                    data={[
                      { period: t('stats.last7Days'), refunds: statistics.refundsLast7Days },
                      { period: t('stats.last30Days'), refunds: statistics.refundsLast30Days }
                    ]}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="refunds" name={t('stats.refundsSeries')} fill="#5469d4" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </>
        ) : (
          <div className="no-data">{t('stats.empty')}</div>
        )}
      </section>

      {/* Recent Refundable Tickets */}
      <section className="recent-tickets-section">
        <div className="section-title-with-hint">
          <h3><FontAwesomeIcon icon={faTicketAlt} /> {t('recent.title', { total: recentTicketsTotalElements })}</h3>
          <p className="table-hint">{t('hint')}</p>
        </div>
        
        {loadingRecentTickets ? (
          <div className="loading-container">
            <FontAwesomeIcon icon={faSpinner} spin size="2x" />
          </div>
        ) : recentTickets.length > 0 ? (
          <>
            <div className="tickets-table-container">
              <table className="tickets-table">
                <thead>
                  <tr>
                    <th>{t('table.ticketId')}</th>
                    <th>{t('table.ibo')}</th>
                    <th>{t('table.event')}</th>
                    <th>{t('table.quantity')}</th>
                    <th>{t('table.amount')}</th>
                    <th>{t('table.payment')}</th>
                    <th>{t('table.date')}</th>
                    <th>{t('table.action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {recentTickets.map(ticket => (
                    <tr 
                      key={ticket.ticketId} 
                      className="ticket-row-clickable"
                      onClick={() => handleOpenTicketDetailModal(ticket)}
                    >
                      <td><strong>{ticket.ticketId}</strong></td>
                      <td>
                        <div className="ibo-cell">
                          <div className="ibo-number">{ticket.iboNumber}</div>
                          <div className="ibo-name">{ticket.iboName || t('notAvailable')}</div>
                        </div>
                      </td>
                      <td>
                        <div className="event-cell">
                          <div className="event-name">{ticket.eventName}</div>
                          <div className="event-date">{formatDate(ticket.eventDate)}</div>
                        </div>
                      </td>
                      <td>{ticket.noOfTickets}</td>
                      <td>{formatCurrency(ticket.totalCost)}</td>
                      <td>{getPaymentMethodBadge(ticket.paymentMethod)}</td>
                      <td>{formatDate(ticket.purchaseDate)}</td>
                      <td>
          <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenRefundModal(ticket);
                          }}
                          className="refund-action-button"
                          disabled={ticket.ticketStatus !== 'ACTIVE'}
                        >
                          <FontAwesomeIcon icon={faMoneyBillWave} />
                          {t('table.refund')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {recentTicketsTotalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => fetchRecentTickets(recentTicketsPage - 1)}
                  disabled={recentTicketsPage === 0}
                  className="pagination-button"
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                  {t('pagination.previous')}
                </button>
                <span className="pagination-info">
                  {t('pagination.pageOf', { page: recentTicketsPage + 1, total: recentTicketsTotalPages })}
                </span>
                <button
                  onClick={() => fetchRecentTickets(recentTicketsPage + 1)}
                  disabled={recentTicketsPage >= recentTicketsTotalPages - 1}
                  className="pagination-button"
                >
                  {t('pagination.next')}
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="no-data">{t('recent.empty')}</div>
        )}
      </section>

      {/* Search Options */}
      <section className="search-section">
        <h3><FontAwesomeIcon icon={faSearch} /> {t('search.title')}</h3>
        
        {/* Search Tabs */}
        <div className="search-tabs">
          <button
            className={`search-tab ${searchTab === 'event' ? 'active' : ''}`}
            onClick={() => {
              setSearchTab('event');
              setError(null);
            }}
          >
            <FontAwesomeIcon icon={faCalendar} />
            {t('search.byEvent')}
          </button>
          <button
            className={`search-tab ${searchTab === 'user' ? 'active' : ''}`}
            onClick={() => {
              setSearchTab('user');
              setError(null);
            }}
          >
            <FontAwesomeIcon icon={faUsers} />
            {t('search.byUser')}
          </button>
        </div>

        {/* Event Search */}
        {searchTab === 'event' && (
          <div className="event-search">
            <div className="search-filters">
              <div className="filter-group">
                <label>{t('search.event')}</label>
                <select
                  value={selectedEventForSearch}
                  onChange={(e) => setSelectedEventForSearch(e.target.value)}
                  disabled={loadingEvents}
                >
                  <option value="">{t('search.eventPlaceholder')}</option>
                  {(() => {
                    // Helper function to format event display text
                    const formatEventDisplay = (event) => {
                      const eventDate = event.startDate ? new Date(event.startDate).toLocaleDateString(intlLocale(), {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      }) : t('search.noDate');
                      
                      let teamsDisplay = t('search.allTeams');
                      if (event.eligibleTeams && event.eligibleTeams.length > 0) {
                        if (event.eligibleTeams.includes('all')) {
                          teamsDisplay = t('search.allTeams');
                        } else if (event.eligibleTeams.length === 1) {
                          teamsDisplay = event.eligibleTeams[0];
                        } else if (event.eligibleTeams.length === 2) {
                          teamsDisplay = event.eligibleTeams.join(', ');
                        } else {
                          teamsDisplay = `${event.eligibleTeams.slice(0, 2).join(', ')} +${event.eligibleTeams.length - 2}`;
                        }
                      }
                      
                      const eventType = enumLabel('eventTypes', event.eventType) || t('notAvailable');
                      return t('search.eventOption', { name: event.name, date: eventDate, teams: teamsDisplay, type: eventType });
                    };
                    
                    // Group events by month and sort
                    const now = new Date();
                    const groupedEvents = {};
                    
                    // First, categorize events as future or past and group by month
                    events.forEach(event => {
                      if (!event.startDate) return;
                      
                      const eventDate = new Date(event.startDate);
                      // Only shown, never parsed: the groups are keyed by sortKey.
                      const monthYear = capitalize(eventDate.toLocaleDateString(intlLocale(), { 
                        month: 'long', 
                        year: 'numeric' 
                      }));
                      const sortKey = `${eventDate.getFullYear()}-${String(eventDate.getMonth() + 1).padStart(2, '0')}`;
                      
                      if (!groupedEvents[sortKey]) {
                        groupedEvents[sortKey] = {
                          label: monthYear,
                          events: [],
                          isFuture: eventDate >= now
                        };
                      }
                      
                      groupedEvents[sortKey].events.push({
                        ...event,
                        eventDate: eventDate
                      });
                    });
                    
                    // Sort events within each month by date
                    Object.values(groupedEvents).forEach(group => {
                      group.events.sort((a, b) => a.eventDate - b.eventDate);
                    });
                    
                    // Sort months: future months first (ascending), then past months (descending)
                    const sortedMonths = Object.keys(groupedEvents).sort((a, b) => {
                      const aIsFuture = groupedEvents[a].isFuture;
                      const bIsFuture = groupedEvents[b].isFuture;
                      
                      // Both future: ascending order (earliest future first)
                      if (aIsFuture && bIsFuture) {
                        return a.localeCompare(b);
                      }
                      
                      // Both past: descending order (most recent past first)
                      if (!aIsFuture && !bIsFuture) {
                        return b.localeCompare(a);
                      }
                      
                      // Future comes before past
                      return aIsFuture ? -1 : 1;
                    });
                    
                    // Render optgroups
                    return sortedMonths.map(sortKey => {
                      const group = groupedEvents[sortKey];
                      const label = group.isFuture 
                        ? t('search.monthUpcoming', { month: group.label })
                        : t('search.monthPast', { month: group.label });
                      
                      return (
                        <optgroup key={sortKey} label={label}>
                          {group.events.map(event => {
                            const displayText = formatEventDisplay(event);
                            return (
                              <option key={event.id} value={event.id} title={displayText}>
                                {displayText}
                              </option>
                            );
                          })}
                        </optgroup>
                      );
                    });
                  })()}
                </select>
            </div>
              
              <div className="filter-group">
                <label>{t('search.status')}</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="">{t('search.allStatuses')}</option>
                  <option value="ACTIVE">{t('ticketStatus.ACTIVE')}</option>
                  <option value="USED">{t('ticketStatus.USED')}</option>
                  <option value="CANCELLED">{t('ticketStatus.CANCELLED')}</option>
                  <option value="EXPIRED">{t('ticketStatus.EXPIRED')}</option>
                </select>
              </div>

            <button
                onClick={handleEventSearchSubmit}
                disabled={!selectedEventForSearch || loadingEventTickets}
                className="search-submit-button"
              >
                {loadingEventTickets ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  {t('search.searching')}
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faSearch} />
                  {t('search.submit')}
                </>
              )}
              </button>
            </div>

            {/* Event Tickets Results */}
            {eventTickets.length > 0 && (
              <div className="search-results">
                <div className="section-title-with-hint">
                  <h4>{t('search.foundTickets', { total: eventTicketsTotalElements })}</h4>
                  <p className="table-hint">{t('hint')}</p>
                </div>
                <div className="tickets-table-container">
                  <table className="tickets-table">
                    <thead>
                      <tr>
                        <th>{t('table.ticketId')}</th>
                        <th>{t('table.ibo')}</th>
                        <th>{t('table.quantity')}</th>
                        <th>{t('table.amount')}</th>
                        <th>{t('table.payment')}</th>
                        <th>{t('table.status')}</th>
                        <th>{t('table.action')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {eventTickets.map(ticket => (
                        <tr 
                          key={ticket.ticketId}
                          className="ticket-row-clickable"
                          onClick={() => handleOpenTicketDetailModal(ticket)}
                        >
                          <td><strong>{ticket.ticketId}</strong></td>
                          <td>
                            <div className="ibo-cell">
                              <div className="ibo-number">{ticket.iboNumber}</div>
                              <div className="ibo-name">{ticket.iboName || t('notAvailable')}</div>
                            </div>
                          </td>
                          <td>{ticket.noOfTickets}</td>
                          <td>{formatCurrency(ticket.totalCost)}</td>
                          <td>{getPaymentMethodBadge(ticket.paymentMethod)}</td>
                          <td>{getStatusBadge(ticket.ticketStatus)}</td>
                          <td>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenRefundModal(ticket);
                              }}
                              className="refund-action-button"
                              disabled={ticket.ticketStatus !== 'ACTIVE'}
                            >
                              <FontAwesomeIcon icon={faMoneyBillWave} />
                              {t('table.refund')}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {eventTicketsTotalPages > 1 && (
                  <div className="pagination">
                    <button
                      onClick={() => fetchEventTickets(selectedEventForSearch, statusFilter, eventTicketsPage - 1)}
                      disabled={eventTicketsPage === 0}
                      className="pagination-button"
                    >
                      <FontAwesomeIcon icon={faChevronLeft} />
                      {t('pagination.previous')}
                    </button>
                    <span className="pagination-info">
                      {t('pagination.pageOf', { page: eventTicketsPage + 1, total: eventTicketsTotalPages })}
                    </span>
                    <button
                      onClick={() => fetchEventTickets(selectedEventForSearch, statusFilter, eventTicketsPage + 1)}
                      disabled={eventTicketsPage >= eventTicketsTotalPages - 1}
                      className="pagination-button"
                    >
                      {t('pagination.next')}
                      <FontAwesomeIcon icon={faChevronRight} />
            </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* User Search */}
        {searchTab === 'user' && (
          <div className="user-search">
            <div className="search-filters">
              <div className="filter-group">
                <label>{t('search.iboOrName')}</label>
              <input
                type="text"
                  placeholder={t('search.iboOrNamePlaceholder')}
                  value={userSearchQuery}
                  onChange={(e) => setUserSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && searchUsers()}
              />
            </div>

              <div className="filter-group">
                <label>{t('search.team')}</label>
              <select
                  value={selectedTeam}
                  onChange={(e) => setSelectedTeam(e.target.value)}
                  disabled={loadingTeams}
                >
                  <option value="">{t('search.allTeamsOption')}</option>
                  {teams.map(team => (
                    <option key={team.id} value={team.name}>
                      {team.name}
                  </option>
                ))}
              </select>
            </div>

            <button
                onClick={searchUsers}
                disabled={!userSearchQuery.trim() || loadingUserSearch}
                className="search-submit-button"
              >
                {loadingUserSearch ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  {t('search.searching')}
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faSearch} />
                  {t('search.submit')}
                </>
              )}
            </button>
            </div>

            {/* User Search Results */}
            {userSearchResults.length > 0 && (
              <div className="user-results">
                <h4>{t('search.foundUsers', { total: userSearchResults.length })}</h4>
                <div className="user-list">
                  {userSearchResults.map(user => (
                    <div
                      key={user.iboNumber}
                      className={`user-card ${selectedUser?.iboNumber === user.iboNumber ? 'selected' : ''}`}
                      onClick={() => handleUserSelect(user)}
                    >
                      <div className="user-info">
                        <div className="user-ibo"><strong>{user.iboNumber}</strong></div>
                        <div className="user-name">{user.iboName || t('notAvailable')}</div>
                        <div className="user-email">{user.email}</div>
                        {user.teamName && <div className="user-team">{t('search.userTeam', { team: user.teamName })}</div>}
                      </div>
                      {selectedUser?.iboNumber === user.iboNumber && (
                        <div className="user-selected-indicator">
                          <FontAwesomeIcon icon={faCheck} />
          </div>
        )}
      </div>
                  ))}
                </div>
              </div>
            )}

            {/* User Tickets */}
            {selectedUser && (
              <div className="user-tickets">
                <div className="user-tickets-header">
                  <div className="section-title-with-hint" style={{ marginBottom: 0 }}>
                    <h4>{t('search.ticketsFor', { name: selectedUser.iboName || selectedUser.iboNumber })}</h4>
                    <p className="table-hint">{t('hint')}</p>
                  </div>
                  <div className="filter-group">
                    <label>{t('search.filterByEvent')}</label>
                    <select
                      value={selectedEventForUser || ''}
                      onChange={(e) => handleEventFilterForUser(e.target.value || null)}
                      disabled={loadingEvents}
                    >
                      <option value="">{t('search.allEvents')}</option>
                      {(() => {
                        // Helper function to format event display text
                        const formatEventDisplay = (event) => {
                          const eventDate = event.startDate ? new Date(event.startDate).toLocaleDateString(intlLocale(), {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          }) : t('search.noDate');
                          
                          let teamsDisplay = t('search.allTeams');
                          if (event.eligibleTeams && event.eligibleTeams.length > 0) {
                            if (event.eligibleTeams.includes('all')) {
                              teamsDisplay = t('search.allTeams');
                            } else if (event.eligibleTeams.length === 1) {
                              teamsDisplay = event.eligibleTeams[0];
                            } else if (event.eligibleTeams.length === 2) {
                              teamsDisplay = event.eligibleTeams.join(', ');
                            } else {
                              teamsDisplay = `${event.eligibleTeams.slice(0, 2).join(', ')} +${event.eligibleTeams.length - 2}`;
                            }
                          }
                          
                          const eventType = enumLabel('eventTypes', event.eventType) || t('notAvailable');
                          return t('search.eventOption', { name: event.name, date: eventDate, teams: teamsDisplay, type: eventType });
                        };
                        
                        // Group events by month and sort
                        const now = new Date();
                        const groupedEvents = {};
                        
                        // First, categorize events as future or past and group by month
                        events.forEach(event => {
                          if (!event.startDate) return;
                          
                          const eventDate = new Date(event.startDate);
                          // Only shown, never parsed: the groups are keyed by sortKey.
                          const monthYear = capitalize(eventDate.toLocaleDateString(intlLocale(), { 
                            month: 'long', 
                            year: 'numeric' 
                          }));
                          const sortKey = `${eventDate.getFullYear()}-${String(eventDate.getMonth() + 1).padStart(2, '0')}`;
                          
                          if (!groupedEvents[sortKey]) {
                            groupedEvents[sortKey] = {
                              label: monthYear,
                              events: [],
                              isFuture: eventDate >= now
                            };
                          }
                          
                          groupedEvents[sortKey].events.push({
                            ...event,
                            eventDate: eventDate
                          });
                        });
                        
                        // Sort events within each month by date
                        Object.values(groupedEvents).forEach(group => {
                          group.events.sort((a, b) => a.eventDate - b.eventDate);
                        });
                        
                        // Sort months: future months first (ascending), then past months (descending)
                        const sortedMonths = Object.keys(groupedEvents).sort((a, b) => {
                          const aIsFuture = groupedEvents[a].isFuture;
                          const bIsFuture = groupedEvents[b].isFuture;
                          
                          // Both future: ascending order (earliest future first)
                          if (aIsFuture && bIsFuture) {
                            return a.localeCompare(b);
                          }
                          
                          // Both past: descending order (most recent past first)
                          if (!aIsFuture && !bIsFuture) {
                            return b.localeCompare(a);
                          }
                          
                          // Future comes before past
                          return aIsFuture ? -1 : 1;
                        });
                        
                        // Render optgroups
                        return sortedMonths.map(sortKey => {
                          const group = groupedEvents[sortKey];
                          const label = group.isFuture 
                            ? t('search.monthUpcoming', { month: group.label })
                            : t('search.monthPast', { month: group.label });
                          
                          return (
                            <optgroup key={sortKey} label={label}>
                              {group.events.map(event => {
                                const displayText = formatEventDisplay(event);
                                return (
                                  <option key={event.id} value={event.id} title={displayText}>
                                    {displayText}
                                  </option>
                                );
                              })}
                            </optgroup>
                          );
                        });
                      })()}
                    </select>
                  </div>
                </div>

                {loadingUserTickets ? (
                  <div className="loading-container">
                    <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                  </div>
                ) : userTickets.length > 0 ? (
                  <>
                    <div className="tickets-table-container">
                      <table className="tickets-table">
              <thead>
                <tr>
                  <th>{t('table.ticketId')}</th>
                  <th>{t('table.event')}</th>
                            <th>{t('table.quantity')}</th>
                            <th>{t('table.amount')}</th>
                            <th>{t('table.payment')}</th>
                  <th>{t('table.status')}</th>
                  <th>{t('table.action')}</th>
                </tr>
              </thead>
              <tbody>
                          {userTickets.map(ticket => (
                            <tr 
                              key={ticket.ticketId}
                              className="ticket-row-clickable"
                              onClick={() => handleOpenTicketDetailModal(ticket)}
                            >
                    <td><strong>{ticket.ticketId}</strong></td>
                    <td>
                      <div className="event-cell">
                        <div className="event-name">{ticket.eventName}</div>
                        <div className="event-date">{formatDate(ticket.eventDate)}</div>
                      </div>
                    </td>
                              <td>{ticket.noOfTickets}</td>
                              <td>{formatCurrency(ticket.totalCost)}</td>
                    <td>{getPaymentMethodBadge(ticket.paymentMethod)}</td>
                              <td>{getStatusBadge(ticket.ticketStatus)}</td>
                    <td>
                      <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenRefundModal(ticket);
                                  }}
                                  className="refund-action-button"
                                  disabled={ticket.ticketStatus !== 'ACTIVE'}
                                >
                                  <FontAwesomeIcon icon={faMoneyBillWave} />
                                  {t('table.refund')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

                    {/* Pagination */}
                    {userTicketsTotalPages > 1 && (
                      <div className="pagination">
                        <button
                          onClick={() => fetchUserTickets(selectedUser.iboNumber, selectedEventForUser, userTicketsPage - 1)}
                          disabled={userTicketsPage === 0}
                          className="pagination-button"
                        >
                          <FontAwesomeIcon icon={faChevronLeft} />
                          {t('pagination.previous')}
                        </button>
                        <span className="pagination-info">
                          {t('pagination.pageOf', { page: userTicketsPage + 1, total: userTicketsTotalPages })}
                        </span>
                        <button
                          onClick={() => fetchUserTickets(selectedUser.iboNumber, selectedEventForUser, userTicketsPage + 1)}
                          disabled={userTicketsPage >= userTicketsTotalPages - 1}
                          className="pagination-button"
                        >
                          {t('pagination.next')}
                          <FontAwesomeIcon icon={faChevronRight} />
                        </button>
        </div>
      )}
                  </>
                ) : (
                  <div className="no-data">{t('search.noUserTickets')}</div>
                )}
          </div>
            )}
          </div>
        )}
      </section>

      {/* Refunds List */}
      <section className="refunds-list-section">
        <div className="section-header">
          <h3><FontAwesomeIcon icon={faFileAlt} /> {t('list.title', { total: refundsTotalElements })}</h3>
          <div className="filter-group">
            <label>{t('list.filterByStatus')}</label>
            <select
              value={refundsStatusFilter}
              onChange={(e) => {
                setRefundsStatusFilter(e.target.value);
                fetchRefunds(0, e.target.value);
              }}
            >
              <option value="">{t('search.allStatuses')}</option>
              <option value="COMPLETED">{t('refundStatus.COMPLETED')}</option>
              <option value="PENDING">{t('refundStatus.PENDING')}</option>
              <option value="FAILED">{t('refundStatus.FAILED')}</option>
              <option value="CANCELLED">{t('refundStatus.CANCELLED')}</option>
            </select>
            </div>
            </div>
        
        {loadingRefunds ? (
          <div className="loading-container">
            <FontAwesomeIcon icon={faSpinner} spin size="2x" />
            </div>
        ) : refunds.length > 0 ? (
          <>
            <div className="tickets-table-container">
              <table className="tickets-table refunds-table">
                <thead>
                  <tr>
                    <th>{t('table.refundId')}</th>
                    <th>{t('table.ticketId')}</th>
                    <th>{t('table.ibo')}</th>
                    <th>{t('table.event')}</th>
                    <th>{t('table.tickets')}</th>
                    <th>{t('table.amount')}</th>
                    <th>{t('table.payment')}</th>
                    <th>{t('table.status')}</th>
                    <th>{t('table.refundedBy')}</th>
                    <th>{t('table.date')}</th>
                  </tr>
                </thead>
                <tbody>
                  {refunds.map(refund => (
                    <tr key={refund.refundId}>
                      <td><strong>{refund.refundId}</strong></td>
                      <td>{refund.ticketId}</td>
                      <td>
                        <div className="ibo-cell">
                          <div className="ibo-number">{refund.iboNumber}</div>
                          <div className="ibo-name">{refund.iboName || t('notAvailable')}</div>
            </div>
                      </td>
                      <td>
                        <div className="event-cell">
                          <div className="event-name">{refund.eventName || t('notAvailable')}</div>
            </div>
                      </td>
                      <td>{refund.ticketsRefunded}</td>
                      <td>{formatCurrency(refund.totalRefund)}</td>
                      <td>{getPaymentMethodBadge(refund.paymentMethod)}</td>
                      <td>{getRefundStatusBadge(refund.status)}</td>
                      <td className="refunded-by">{refund.refundedBy}</td>
                      <td>{formatDate(refund.refundDate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
          </div>

            {/* Pagination */}
            {refundsTotalPages > 1 && (
              <div className="pagination">
            <button
                  onClick={() => fetchRefunds(refundsPage - 1, refundsStatusFilter)}
                  disabled={refundsPage === 0}
                  className="pagination-button"
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                  {t('pagination.previous')}
                </button>
                <span className="pagination-info">
                  {t('pagination.pageOf', { page: refundsPage + 1, total: refundsTotalPages })}
                </span>
                <button
                  onClick={() => fetchRefunds(refundsPage + 1, refundsStatusFilter)}
                  disabled={refundsPage >= refundsTotalPages - 1}
                  className="pagination-button"
                >
                  {t('pagination.next')}
                  <FontAwesomeIcon icon={faChevronRight} />
            </button>
        </div>
      )}
          </>
        ) : (
          <div className="no-data">{t('list.empty')}</div>
        )}
      </section>

      {/* Ticket Detail Modal */}
      {showTicketDetailModal && selectedTicketDetails && (
        <div className="modal-overlay" onClick={handleCloseTicketDetailModal}>
          <div className="modal-content ticket-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <FontAwesomeIcon icon={faTicketAlt} />
                {t('details.title')}
            </h3>
              <button className="close-button" onClick={handleCloseTicketDetailModal}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
          </div>

            <div className="modal-body">
              {/* Ticket Information */}
              <div className="detail-section">
                <h4><FontAwesomeIcon icon={faTicketAlt} /> {t('details.ticketInfo')}</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">{t('details.ticketId')}</span>
                    <span className="detail-value">{selectedTicketDetails.ticketId}</span>
            </div>
                  <div className="detail-item">
                    <span className="detail-label">{t('details.ticketType')}</span>
                    <span className="detail-value">{enumLabel('ticketTypes', selectedTicketDetails.ticketType) || t('notAvailable')}</span>
            </div>
                  <div className="detail-item">
                    <span className="detail-label">{t('details.numberOfTickets')}</span>
                    <span className="detail-value">{selectedTicketDetails.noOfTickets}</span>
            </div>
                  <div className="detail-item">
                    <span className="detail-label">{t('details.status')}</span>
                    <span className="detail-value">{getStatusBadge(selectedTicketDetails.ticketStatus)}</span>
            </div>
                  <div className="detail-item">
                    <span className="detail-label">{t('details.purchaseDate')}</span>
                    <span className="detail-value">{formatDate(selectedTicketDetails.purchaseDate)}</span>
            </div>
            </div>
            </div>

              {/* IBO Information */}
              <div className="detail-section">
                <h4><FontAwesomeIcon icon={faIdBadge} /> {t('details.iboInfo')}</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">{t('details.iboNumber')}</span>
                    <span className="detail-value">{selectedTicketDetails.iboNumber}</span>
            </div>
                  <div className="detail-item">
                    <span className="detail-label">{t('details.iboName')}</span>
                    <span className="detail-value">{selectedTicketDetails.iboName || t('notAvailable')}</span>
            </div>
                  <div className="detail-item">
                    <span className="detail-label">{t('details.email')}</span>
                    <span className="detail-value">{selectedTicketDetails.userEmail || t('notAvailable')}</span>
            </div>
                  <div className="detail-item">
                    <span className="detail-label">{t('details.team')}</span>
                    <span className="detail-value">{selectedTicketDetails.teamName || t('notAvailable')}</span>
            </div>
                </div>
                </div>

              {/* Event Information */}
              <div className="detail-section">
                <h4><FontAwesomeIcon icon={faCalendar} /> {t('details.eventInfo')}</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">{t('details.eventId')}</span>
                    <span className="detail-value">{selectedTicketDetails.eventId}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">{t('details.eventName')}</span>
                    <span className="detail-value">{selectedTicketDetails.eventName}</span>
              </div>
                  <div className="detail-item">
                    <span className="detail-label">{t('details.eventDate')}</span>
                    <span className="detail-value">{formatDate(selectedTicketDetails.eventDate)}</span>
                  </div>
                </div>
              </div>

              {/* Payment Information */}
              <div className="detail-section">
                <h4><FontAwesomeIcon icon={faMoneyBillWave} /> {t('details.paymentInfo')}</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">{t('details.orderId')}</span>
                    <span className="detail-value">{selectedTicketDetails.orderId || t('notAvailable')}</span>
              </div>
                  <div className="detail-item">
                    <span className="detail-label">{t('details.paymentId')}</span>
                    <span className="detail-value">{selectedTicketDetails.paymentId || t('notAvailable')}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">{t('details.paymentMethod')}</span>
                    <span className="detail-value">{getPaymentMethodBadge(selectedTicketDetails.paymentMethod)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">{t('details.paymentStatus')}</span>
                    <span className="detail-value">{enumLabel('paymentStatus', selectedTicketDetails.paymentStatus) || t('notAvailable')}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">{t('details.totalCost')}</span>
                    <span className="detail-value highlight-amount">{formatCurrency(selectedTicketDetails.totalCost)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">{t('details.currency')}</span>
                    <span className="detail-value">{selectedTicketDetails.currency || 'USD'}</span>
                  </div>
                </div>
              </div>
          </div>

            <div className="modal-footer">
            <button
                onClick={handleCloseTicketDetailModal}
                className="cancel-button"
            >
              {t('details.close')}
            </button>
              {selectedTicketDetails.ticketStatus === 'ACTIVE' && (
                <button
                  onClick={() => {
                    handleCloseTicketDetailModal();
                    handleOpenRefundModal(selectedTicketDetails);
                  }}
                  className="submit-button"
                >
                  <FontAwesomeIcon icon={faMoneyBillWave} />
                  {t('details.processRefund')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Refund Modal */}
      {showRefundModal && selectedTicketForRefund && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>
                <FontAwesomeIcon icon={faMoneyBillWave} />
                {t('modal.title')}
              </h3>
              <button className="close-button" onClick={handleCloseRefundModal}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="modal-body">
              <div className="ticket-summary">
                <h4>{t('modal.summary')}</h4>
                <p><strong>{t('modal.ticketId')}</strong> {selectedTicketForRefund.ticketId}</p>
                <p><strong>{t('modal.event')}</strong> {selectedTicketForRefund.eventName}</p>
                <p><strong>{t('modal.ibo')}</strong> {t('modal.iboValue', { number: selectedTicketForRefund.iboNumber, name: selectedTicketForRefund.iboName || t('notAvailable') })}</p>
                <p><strong>{t('modal.availableTickets')}</strong> {selectedTicketForRefund.noOfTickets}</p>
                <p><strong>{t('modal.paymentMethod')}</strong> {enumLabel('paymentMethods', selectedTicketForRefund.paymentMethod)}</p>
              </div>

              <div className="form-group">
                <label htmlFor="numberOfTickets">{t('modal.numberOfTickets')}</label>
                <input
                  type="number"
                  id="numberOfTickets"
                  min="1"
                  max={selectedTicketForRefund.noOfTickets}
                  value={numberOfTickets}
                  onChange={(e) => setNumberOfTickets(parseInt(e.target.value) || 1)}
                  disabled={processing}
                />
                <small>{t('modal.maximum', { max: selectedTicketForRefund.noOfTickets })}</small>
              </div>

              <div className="refund-preview">
                <h4>{t('modal.preview')}</h4>
                <div className="preview-row total">
                  <span><strong>{t('modal.estimatedAmount')}</strong></span>
                  <span><strong>${getEstimatedRefundTotal()}</strong></span>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="reason">{t('modal.reason')}</label>
                <textarea
                  id="reason"
                  maxLength="500"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={t('modal.reasonPlaceholder')}
                  rows="3"
                  disabled={processing}
                />
                <small>{t('modal.reasonCount', { length: reason.length })}</small>
              </div>

              <div className="form-group">
                <label htmlFor="notes">{t('modal.notes')}</label>
                <textarea
                  id="notes"
                  maxLength="1000"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={t('modal.notesPlaceholder')}
                  rows="3"
                  disabled={processing}
                />
                <small>{t('modal.notesCount', { length: notes.length })}</small>
              </div>

              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={confirmChecked}
                    onChange={(e) => setConfirmChecked(e.target.checked)}
                    disabled={processing}
                  />
                  <span>{t('modal.confirm')}</span>
                </label>
              </div>
            </div>

            <div className="modal-footer">
              <button
                onClick={handleCloseRefundModal}
                disabled={processing}
                className="cancel-button"
              >
                {t('modal.cancel')}
              </button>
              <button
                onClick={handleProcessRefund}
                disabled={processing || !confirmChecked}
                className="submit-button"
              >
                {processing ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    {t('modal.processing')}
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faCheck} />
                    {t('modal.submit')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Refund Result (kept from existing) */}
      {refundResult && (
        <div className="refund-result-card">
          <div className="card-header success-header">
            <h3>
              <FontAwesomeIcon icon={faCheck} />
              {t('result.title')}
            </h3>
          </div>

          <div className="result-content">
            <div className="result-item">
              <span className="result-label">{t('result.refundId')}</span>
              <span className="result-value highlight">{refundResult.refundId}</span>
            </div>
            <div className="result-item">
              <span className="result-label">{t('result.refundType')}</span>
              <span className="result-value">{enumLabel('refundTypes', refundResult.refundType)}</span>
            </div>
            <div className="result-item">
              <span className="result-label">{t('result.ticketsRefunded')}</span>
              <span className="result-value">{refundResult.ticketsRefunded}</span>
            </div>
            <div className="result-item">
              <span className="result-label">{t('result.totalRefund')}</span>
              <span className="result-value">${refundResult.totalRefund?.toFixed(2)}</span>
            </div>
          </div>

          <div className="card-actions">
            <button
              onClick={() => setRefundResult(null)}
              className="close-result-button"
            >
              {t('result.close')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RefundManagementPage;

